import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Stack } from '@tanium/react-stack';
import { TextInput } from '@tanium/react-text-input';
import { Checkbox } from '@tanium/react-checkbox';
import { StackedRibbonListMinimal, StackedRibbonListWrapper } from '@tanium/react-stacked-ribbon-list';
import { LinkText } from '../LinkText';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { MediumSubtleText } from '../../TextStyles';
import { IdentityProviderGroupDescription } from './IdentityProviderGroupDescription';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { useComplianceFramework } from '../../hooks/useComplianceFramework';
import { ComplianceFramework } from '../../types';
import { DocLinks } from '../../utilities/constants';

interface RenderGroupAttributeProps {
  selectedRoleIds: Set<string>;
  setSelectedRoleIds: (selectedSuiteIds: Set<string>) => void;
  roleMapperData: ConfigureIdpRequest['roleMappers'];
  setRoleMapperData: (roleMapperData: ConfigureIdpRequest['roleMappers']) => void;
}

export type UserRolesRow = {
  userRole: string;
  selected: JSX.Element;
  schema: any;
  value: any;
};

// Define the mapping object
const roleMapping: { [key: string]: string } = {
  audit_user: 'Artifacts User',
  admin: 'Admin',
};

// Function to map roles
const mapRole = (role: string): string => {
  return roleMapping[role] || 'Unknown Role';
};

export const RenderGroupAttribute: React.VFC<RenderGroupAttributeProps> = ({
  selectedRoleIds,
  setSelectedRoleIds,
  roleMapperData,
  setRoleMapperData,
}) => {
  const { t } = useTranslation();
  const [displayRows, setDisplayRows] = useState<UserRolesRow[]>([]);
  const { idpRoles } = useIdentityProviderConfigs();
  const isGov = useComplianceFramework() === ComplianceFramework.USGov;
  const visibleIdpRoles = useMemo(
    () => (isGov ? idpRoles.filter((role) => role !== 'audit_user') : idpRoles),
    [idpRoles, isGov],
  );
  const displayColumns = [
    {
      label: t('User Role'),
      dataKey: 'userRole',
      primary: true,
      width: '15%',
    },
    {
      label: t('Claim Attribute Name'),
      helpText: t('Group Claim Attribute'),
      dataKey: 'schema',
    },
    {
      label: t('Claim Value'),
      helpText: t('Claim value for users who should be assigned the Tanium Account role.'),
      dataKey: 'value',
    },
  ];
  const roleRows = useMemo(() => {
    return visibleIdpRoles.map((role) => {
      return {
        userRole: mapRole(role),
        selected: (
          <Checkbox
            data-testid={`editable-value-checkbox-${role}`}
            checked={selectedRoleIds.has(role)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedRoleIds(new Set([...selectedRoleIds, role]));
                const newRoleMapperData = { ...roleMapperData };
                newRoleMapperData[role] = {
                  key: '',
                  value: '',
                };
                setRoleMapperData(newRoleMapperData);
              } else {
                const newSelection = new Set(selectedRoleIds);
                newSelection.delete(role);
                setSelectedRoleIds(newSelection);

                const newRoleMapperData = { ...roleMapperData };
                delete newRoleMapperData[role];
                setRoleMapperData(newRoleMapperData);
              }
            }}
          />
        ),
        schema: selectedRoleIds.has(role) && (
          <TextInput
            data-testid={`editable-value-input-${role}-key`}
            style={{ width: '300px' }}
            value={roleMapperData?.[role]?.key}
            onChange={(e) => {
              const newRoleMapperData = { ...roleMapperData };
              newRoleMapperData[role] = {
                ...newRoleMapperData[role],
                key: e.target.value,
              };
              setRoleMapperData(newRoleMapperData);
            }}
            required={true}
          />
        ),
        value: selectedRoleIds.has(role) && (
          <TextInput
            data-testid={`editable-value-input-${role}-value`}
            style={{ width: '300px' }}
            value={roleMapperData?.[role]?.value}
            onChange={(e) => {
              const newRoleMapperData = { ...roleMapperData };
              newRoleMapperData[role] = {
                ...newRoleMapperData[role],
                value: e.target.value,
              };
              setRoleMapperData(newRoleMapperData);
            }}
            required={true}
          />
        ),
      };
    });
  }, [visibleIdpRoles, selectedRoleIds, roleMapperData, setSelectedRoleIds, setRoleMapperData]);

  useEffect(() => {
    setDisplayRows([...roleRows]);
  }, [roleRows, roleMapperData, setRoleMapperData]);

  const dataGetter = useCallback((i: number) => displayRows[i], [displayRows]);
  return (
    <Stack direction="column" itemSpacing={1}>
      <MediumSubtleText>
        <Trans
          i18nKey="Configure the user role assigned to users who sign in through this IDP."
          components={{
            docLink: <LinkText to={DocLinks.IDP_ROLE_ASSIGN} />,
          }}
        >
          {/* tests fail unless something is in here */}
          Configure the user role assigned to users who sign in through this IDP.
        </Trans>
      </MediumSubtleText>
      <IdentityProviderGroupDescription />
      <StackedRibbonListWrapper height="130px">
        <StackedRibbonListMinimal
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          columns={[{ dataKey: 'selected', label: '', width: '24px' }, ...displayColumns] as any}
          isItemLoaded={() => true}
          itemCount={displayRows.length}
          dataGetter={dataGetter}
          itemIdGetter={(i) => dataGetter(i).userRole}
          sort={[]}
          selection={selectedRoleIds}
        />
      </StackedRibbonListWrapper>
    </Stack>
  );
};
